import React, { useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Play,
  Copy,
  Save,
  RefreshCw,
  Cpu,
  Clock,
  DollarSign,
  Hash,
  FileText,
  Sparkles,
  CheckCircle,
  XCircle
} from 'lucide-react';
import type { TestCase, TestSession, ModelResponse, AgentType, ModelType } from '@/types/ai-admin';
import { createTestSession, callTestQueryEdgeFunction, updateTestSession } from '@/services/aiTestingService';
import { createComponentLogger } from '@/utils/logger';
import { toast } from 'sonner';

const log = createComponentLogger('QueryTester', 'ai-testing');

interface QueryTesterProps {
  testCases: TestCase[];
  onSessionComplete: (session: TestSession) => void;
}

export default function QueryTester({ testCases, onSessionComplete }: QueryTesterProps) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');

  const [query, setQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentType>('encompass_helper');
  const [selectedModel, setSelectedModel] = useState<ModelType>('claude-haiku-4-5-20251001');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1000);
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<ModelResponse | null>(null);
  const [selectedTestCase, setSelectedTestCase] = useState<string>('');

  // Admin-only access for test console
  if (!isAdmin) {
    return (
      <Card className="p-6">
        <div className="text-center text-muted-foreground">
          <p>Test console is restricted to administrators.</p>
        </div>
      </Card>
    );
  }

  const models: Array<{ value: ModelType; label: string; cost: number }> = [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude 4.5 Haiku', cost: 1.00 },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude 4.5 Sonnet', cost: 3.00 },
    { value: 'claude-opus-4-1-20250805', label: 'Claude 4.1 Opus', cost: 15.00 }
  ];

  const agents: Array<{ value: AgentType; label: string }> = [
    { value: 'encompass_helper', label: 'Encompass Helper' },
    { value: 'pre_approval', label: 'Pre-Approval Assistant' },
    { value: 'document_checklist', label: 'Document Checklist' },
    { value: 'mortgage_education', label: 'Mortgage Education' },
    { value: 'investor_guidelines', label: 'Investor Guidelines' },
    { value: 'wisr', label: 'Wisr Assistant' },
    { value: 'general', label: 'General Assistant' }
  ];

  // Load test case
  const loadTestCase = (caseId: string) => {
    const testCase = testCases.find(tc => tc.id === caseId);
    if (testCase) {
      setQuery(testCase.query);
      setSelectedAgent(testCase.agentType);
      setSelectedTestCase(caseId);
    }
  };

  // Run query
  const runQuery = async () => {
    if (!query.trim()) {
      log.warn('Cannot run query: empty query string');
      return;
    }

    setIsLoading(true);
    const startTime = Date.now();

    try {
      log.info('Running test query', { agent: selectedAgent, model: selectedModel });

      // Create test session
      const sessionId = await createTestSession(
        selectedTestCase ? [selectedTestCase] : [],
        'manual'
      );

      // Call edge function for real AI response
      const modelResponse = await callTestQueryEdgeFunction(
        query,
        selectedModel,
        selectedAgent,
        sessionId,
        selectedTestCase || undefined
      );

      setResponse(modelResponse);

      const totalDuration = Date.now() - startTime;

      // Update session with results
      await updateTestSession(sessionId, {
        totalDuration,
        totalCost: modelResponse.cost,
        successRate: modelResponse.success ? 100 : 0
      });

      // Create session object for parent component
      const session: TestSession = {
        id: sessionId,
        timestamp: new Date().toISOString(),
        testCases: selectedTestCase ? [selectedTestCase] : [],
        responses: [modelResponse],
        totalDuration,
        totalCost: modelResponse.cost,
        successRate: modelResponse.success ? 100 : 0
      };

      onSessionComplete(session);
      log.info('Test query completed', {
        responseTime: modelResponse.responseTime,
        cost: modelResponse.cost
      });

    } catch (error) {
      log.error('Test query failed', {}, error as Error);

      // Show error response
      setResponse({
        id: `error_${Date.now()}`,
        testCaseId: selectedTestCase || undefined,
        query,
        model: selectedModel,
        response: `Error: ${(error as Error).message}`,
        responseTime: Date.now() - startTime,
        cost: 0,
        tokensInput: 0,
        tokensOutput: 0,
        success: false,
        documentsUsed: [],
        timestamp: new Date().toISOString()
      });

      toast.error(`Query failed: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };


  // Copy response to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Save as test case
  const saveAsTestCase = async () => {
    if (!query || !response) {
      log.warn('Cannot save: missing query or response');
      return;
    }

    try {
      log.info('Saving query as test case');

      const { createTestCase } = await import('@/services/aiTestingService');

      await createTestCase({
        name: `Test: ${query.substring(0, 50)}${query.length > 50 ? '...' : ''}`,
        description: 'Saved from query tester',
        query,
        expectedKeywords: [],
        agentType: selectedAgent,
        tags: ['saved', 'manual']
      });

      toast.success('Test case saved. Switch to the Test Cases tab to view it.');
      log.info('Test case saved successfully');
    } catch (error) {
      log.error('Failed to save test case', {}, error as Error);
      toast.error(`Failed to save test case: ${(error as Error).message}`);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Left Panel - Query Input */}
      <Card>
        <CardHeader>
          <CardTitle>Query Input</CardTitle>
          <CardDescription>
            Enter a query to test or select a predefined test case
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Test Case Selection */}
          {testCases.length > 0 && (
            <div className="space-y-2">
              <Label>Load Test Case (Optional)</Label>
              <Select value={selectedTestCase} onValueChange={loadTestCase}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a test case..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {testCases.map(tc => (
                    <SelectItem key={tc.id} value={tc.id}>
                      {tc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Query Input */}
          <div className="space-y-2">
            <Label htmlFor="query">Query</Label>
            <Textarea
              id="query"
              placeholder="Enter your test query..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              rows={4}
            />
          </div>

          {/* Agent Selection */}
          <div className="space-y-2">
            <Label>Agent</Label>
            <Select value={selectedAgent} onValueChange={(v) => setSelectedAgent(v as AgentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map(agent => (
                  <SelectItem key={agent.value} value={agent.value}>
                    {agent.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Model Selection */}
          <div className="space-y-2">
            <Label>Model</Label>
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {models.map(model => (
                  <SelectItem key={model.value} value={model.value}>
                    <div className="flex items-center justify-between w-full">
                      <span>{model.label}</span>
                      <Badge variant="outline" className="ml-2">
                        ${model.cost}/1M tokens
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Temperature */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Temperature</Label>
              <span className="text-sm text-muted-foreground">{temperature}</span>
            </div>
            <Slider
              value={[temperature]}
              onValueChange={([v]) => setTemperature(v)}
              min={0}
              max={1}
              step={0.1}
            />
          </div>

          {/* Max Tokens */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Max Tokens</Label>
              <span className="text-sm text-muted-foreground">{maxTokens}</span>
            </div>
            <Slider
              value={[maxTokens]}
              onValueChange={([v]) => setMaxTokens(v)}
              min={100}
              max={4000}
              step={100}
            />
          </div>

          {/* Include Documents */}
          <div className="flex items-center justify-between">
            <Label htmlFor="include-docs">Include Documents</Label>
            <Switch
              id="include-docs"
              checked={includeDocuments}
              onCheckedChange={setIncludeDocuments}
            />
          </div>

          {/* Run Button */}
          <Button
            className="w-full"
            onClick={runQuery}
            disabled={!query.trim() || isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Query
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Right Panel - Response */}
      <Card>
        <CardHeader>
          <CardTitle>Response</CardTitle>
          <CardDescription>
            View the AI response and metadata
          </CardDescription>
        </CardHeader>
        <CardContent>
          {response ? (
            <div className="space-y-4">
              {/* Response Content */}
              <div className="space-y-2">
                <Label>Response</Label>
                <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                  <p className="whitespace-pre-wrap text-sm">{response.response}</p>
                </ScrollArea>
              </div>

              {/* Metadata */}
              <div className="space-y-2">
                <Label>Metadata</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{(response.responseTime / 1000).toFixed(2)}s</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">${response.cost.toFixed(4)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{response.tokensInput}/{response.tokensOutput} tokens</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Cpu className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{response.model}</span>
                  </div>
                </div>
              </div>

              {/* Documents Used */}
              {response.documentsUsed && response.documentsUsed.length > 0 && (
                <div className="space-y-2">
                  <Label>Documents Used</Label>
                  <div className="space-y-1">
                    {response.documentsUsed.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          <span className="truncate" title={doc.title || doc.documentId}>
                            {doc.title || doc.documentId}
                          </span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {(doc.relevanceScore * 100).toFixed(0)}% match
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success Indicator */}
              <div className="flex items-center space-x-2">
                {response.success ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Query successful</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-600">Query failed</span>
                  </>
                )}
                {response.score && (
                  <Badge variant="outline" className="ml-auto">
                    Score: {(response.score * 100).toFixed(0)}%
                  </Badge>
                )}
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(response.response)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={saveAsTestCase}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save as Test Case
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[400px] text-center">
              <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {isLoading ? 'Processing your query...' : 'Run a query to see the response'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
